import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

import { StripeSubscription } from "models/UserSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import { getStripeSubscription } from "store/userSetting/actions";
import styles from "./style.module.scss";
import HasSubscriptionCard from "./HasSubscriptionCard";
import {
  isSubscriptionPlanType,
  SubscriptionPlanType,
  SubscriptionPlan,
  ProductCatalog,
  PlanDetails
} from "helpers/subscriptionPlans";
import penniesToDollars from "helpers/convertPenniesToDollars";
import formatTextUpperCase from "helpers/formatTextUpperCase";
import { useProductCatalog } from "hooks/useProductCatalog";
import { RefLoadingSpinner } from "components/LoadingSpinner";

interface IProps {
  onChangeSubscriptionPlanType: (plan: string) => void;
  onHandleDeactivatePlan: VoidFunction;
  onHandleUpdatePlan: VoidFunction;
  setPlanDetails: (plan: PlanDetails) => void;
}

export default function SubscriptionPlanCard({
  onChangeSubscriptionPlanType,
  onHandleDeactivatePlan,
  onHandleUpdatePlan,
  setPlanDetails
}: Readonly<IProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const [planMethod, setPlanMethod] = useState("annually");

  const { stripeSubscription: hasSubscription } = useSelector(
    (state: RootState) => state.userSettingState
  );

  const {
    plan_type: planType,
    amount,
    amount_per_month,
    days_left
  } = useMemo<Partial<StripeSubscription>>(() => {
    if (hasSubscription) {
      return hasSubscription;
    } else return {};
  }, [hasSubscription]);

  const handleClickPlan = (planType: string, plan: PlanDetails) => {
    setPlanMethod(planType);
    onChangeSubscriptionPlanType(planType);
    setPlanDetails(plan);
  };

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      dispatch(handleLoading(true));
      await dispatch(getStripeSubscription()).finally(() => dispatch(handleLoading(false)));
    };

    fetchSubscriptionData();
  }, []);

  const planName: SubscriptionPlanType = isSubscriptionPlanType(planType) ? planType : "annually";
  const { subscriptionPlan, loading } = useProductCatalog<ProductCatalog>();

  useEffect(() => {
    !loading && subscriptionPlan && setPlanDetails(subscriptionPlan[planMethod] as PlanDetails);
  }, [loading, subscriptionPlan]);

  if (hasSubscription)
    return (
      <HasSubscriptionCard
        onHandleDeactivatePlan={onHandleDeactivatePlan}
        planName={planName}
        planAmount={penniesToDollars(amount !== undefined ? Number(amount) : 0)}
        planAmountPerMonth={penniesToDollars(amount !== undefined ? Number(amount_per_month) : 0)}
        planDetails={subscriptionPlan[planMethod]?.planPriceType}
        onHandleUpdatePlan={onHandleUpdatePlan}
        leftDay={days_left ? days_left : 0}
      />
    );

  const tabIndicatorStyle = () => {
    switch (planMethod) {
      case "annually":
        return "translate-x-0";
      case "six_month":
        return "translate-x-1/2";
      default:
        return "translate-x-full";
    }
  };
  return (
    <ul className={styles.billingTabSwitchContainer}>
      {loading && <RefLoadingSpinner isLoadingMore={loading} observerRef={null} />}
      {subscriptionPlan &&
        Object.entries(subscriptionPlan as SubscriptionPlan).map(([planType, plan]) => (
          <button
            key={plan.planKey}
            className={clsx(
              styles.cardWrapper,
              planMethod === planType ? styles.selectedCard : styles.unselectedCard
            )}
            onClick={() => handleClickPlan(planType as SubscriptionPlanType, plan)}>
            <p className="pb-1">{formatTextUpperCase(planType)}</p>
            <p>
              <b className="text-2xl leading-6">{plan.planPriceByMonth}</b> /month
            </p>
            <p>
              <span className="text-xs italic">{plan.planLabel}</span>
            </p>
          </button>
        ))}

      {/* Tab Indicator */}
      <div className={clsx(styles.tabIndicator, tabIndicatorStyle())}></div>
    </ul>
  );
}
